import type { MedRequestMutationFnProps } from '@/lib/api/med-requests/action'
import { afterApprovalStatuses, afterRejectionStatuses } from '@/lib/constants/med-requests'
import { shouldLockMedRequest } from '@/lib/permissions'
import type { TreatmentDecision } from '@/lib/stores/medication-request'
import {
  isLinkMedRequest,
  isMedRequestInEditableStage,
  isMpsMedRequest,
  isMultiProductOrderMedRequest
} from '@/lib/utils'
import type { Medication, MedRequest } from '@/types/data'

/**
 * Every order of a multiproduct subscription shares this order-number prefix — `evaro-562265#MPS`
 * is the subscription, `#MPSORD#1` an order, `#MPSMED#2` a medication added later. Each is its own
 * order with its own hash, so the prefix is the only thing that groups the family (matches the
 * API's getMpsOrderNumberBase).
 */
const MPS_ORDER_NUMBER_TOKEN = '#MPS'

/**
 * The family a med request is reviewed with, or null: an MPS subscription by order-number prefix
 * (or order hash), a multi product order by its consultation answers. LINK and OTC items have none.
 */
export const familyKeyOf = (medRequest: MedRequest): string | null => {
  if (isMpsMedRequest(medRequest)) {
    const tokenIndex = medRequest.orderNumber?.toUpperCase().indexOf(MPS_ORDER_NUMBER_TOKEN) ?? -1
    const key = tokenIndex === -1 ? medRequest.orderHash : medRequest.orderNumber.slice(0, tokenIndex)
    return key ? `mps:${key}` : null
  }

  return orderFamilyKeyOf(medRequest)
}

/** The multi product order a med request belongs to, by its consultation answers; null for MPS, LINK and OTC. */
export const orderFamilyKeyOf = (medRequest: MedRequest): string | null => {
  if (isMpsMedRequest(medRequest) || isLinkMedRequest(medRequest) || !medRequest.orderHash || !medRequest.answerHash) {
    return null
  }
  return `order:${medRequest.orderHash}:${medRequest.answerHash}`
}

/** A decision's actions: awaited one by one as a chain, or a lone action fired while the page moves on. */
export type ActionPlan = {
  actions: MedRequestMutationFnProps[]
  isChain: boolean
}

/** The opened request's action alone is fired like on any other request; anything else is a chain. */
export const actionPlanOf = (actions: MedRequestMutationFnProps[], openedAction: MedRequestMutationFnProps) => ({
  actions,
  isChain: !(actions.length === 1 && actions[0].id === openedAction.id)
})

/** The approve's product change when the picked medication differs from the ordered one. */
export const changeProductFor = (medRequest: MedRequest, medication: Medication | null) => {
  const selectedHash = medication?.variantHash || medication?.productHash || ''
  const orderedHash = medRequest.orderedItem?.id

  return selectedHash && selectedHash !== orderedHash ? { prevSKU: orderedHash || '', newSKU: selectedHash } : undefined
}

/** Stage an order item waits in for the prescriber's decision. */
const CLINICAL_REVIEW_STAGE = 'clinical review'

export const isInClinicalReview = (medRequest: { stage?: string | null }) =>
  medRequest.stage?.toLowerCase() === CLINICAL_REVIEW_STAGE

/** Held items stay pending: NodeAPI's FAN only brings back the item it was started for. */
const PENDING_STAGES = [CLINICAL_REVIEW_STAGE, 'clinical further answers needed']

const isAwaitingDecision = (medRequest: { stage?: string | null }) =>
  PENDING_STAGES.includes(medRequest.stage?.toLowerCase() ?? '')

/** The order's treatments in card order: the opened request, then its other items. */
export const multiProductOrderTreatmentRequestsOf = (medRequest: MedRequest): MedRequest[] => [
  medRequest,
  ...(medRequest.orderMedRequests ?? [])
]

/**
 * The order's other items in clinical review or on hold, which the "all" actions decide with the
 * opened request. Empty outside multi product orders.
 */
export const pendingMultiProductOrderFamilyRows = (medRequest: MedRequest): MedRequest[] => {
  if (!isMultiProductOrderMedRequest(medRequest)) return []

  return (medRequest.orderMedRequests ?? []).filter(isAwaitingDecision)
}

/** The order's other items at an editable stage, whose cards can record a decision. */
export const decidableMultiProductOrderFamilyRows = (medRequest: MedRequest): MedRequest[] => {
  if (!isMultiProductOrderMedRequest(medRequest)) return []

  return (medRequest.orderMedRequests ?? []).filter(row => isMedRequestInEditableStage(row.stage ?? ''))
}

/** Stages before an item reaches the prescriber, including a DA auto-FAN awaiting answers. */
const AWAITING_REVIEW_STAGES = [
  'duplicate check',
  'system id check',
  'system scr check',
  'decision assistant check',
  'system fan check',
  'awaiting vendor decision',
  'clinical further answers needed'
]

const isAwaitingReview = (medRequest: { stage?: string | null }) =>
  AWAITING_REVIEW_STAGES.includes(medRequest.stage?.toLowerCase() ?? '')

/** An item waits for the prescriber while another hasn't reached review yet. */
export const isMultiProductOrderAwaitingItems = (medRequest?: MedRequest) => {
  if (!medRequest || !isMultiProductOrderMedRequest(medRequest)) return false

  const treatments = multiProductOrderTreatmentRequestsOf(medRequest)
  return treatments.some(isInClinicalReview) && treatments.some(isAwaitingReview)
}

export type ReadOnlyReason = 'awaiting-items' | 'previous-request'

/** Why nothing on the page is actionable, or null when something is. */
export const readOnlyReasonOf = (medRequest: MedRequest): ReadOnlyReason | null => {
  if (isMultiProductOrderAwaitingItems(medRequest)) return 'awaiting-items'

  const isActionable =
    isMedRequestInEditableStage(medRequest.stage ?? '') || decidableMultiProductOrderFamilyRows(medRequest).length > 0
  return isActionable ? null : 'previous-request'
}

/** Opening the request locks the order if this role locks it or any item its cards can still decide. */
export const shouldLockOpenedMedRequest = (medRequest: MedRequest) =>
  !isMultiProductOrderAwaitingItems(medRequest) &&
  (shouldLockMedRequest(medRequest) || decidableMultiProductOrderFamilyRows(medRequest).some(shouldLockMedRequest))

/**
 * Whether the opened request still awaits a decision; it may have been decided on its own card.
 * Always true outside multi product orders.
 */
export const isOpenedTreatmentPending = (medRequest: MedRequest) =>
  !isMultiProductOrderMedRequest(medRequest) || isAwaitingDecision(medRequest)

/** Stages reached once approved (or further, e.g. on hold) or rejected. */
const ACTIONED_STAGES = [...afterApprovalStatuses, ...afterRejectionStatuses].map(stage => stage.toLowerCase())

/** Whether the opened request was already approved or rejected, e.g. on its own card. */
const isOpenedTreatmentActioned = (medRequest: { stage?: string | null }) =>
  ACTIONED_STAGES.includes(medRequest.stage?.toLowerCase() ?? '')

export type FamilyDecision = 'reject' | 'hold'

/** The pending treatments; with none left, Follow Up holds the decidable ones, as on a single request. */
export const familyDecisionTargetsOf = (medRequest: MedRequest, action: FamilyDecision) => {
  const isOpenedPending = isOpenedTreatmentPending(medRequest)
  const pendingRows = pendingMultiProductOrderFamilyRows(medRequest)
  if (action === 'reject' || isOpenedPending || pendingRows.length > 0) {
    return { isOpenedTarget: isOpenedPending, rows: pendingRows }
  }

  return {
    isOpenedTarget: isMedRequestInEditableStage(medRequest.stage ?? ''),
    rows: decidableMultiProductOrderFamilyRows(medRequest)
  }
}

export const hasMultiProductOrderFollowUpTarget = (medRequest: MedRequest) => {
  const { isOpenedTarget, rows } = familyDecisionTargetsOf(medRequest, 'hold')
  return isOpenedTarget || rows.length > 0
}

/** The opened request was already actioned, but other items still await Reject all / Follow Up. */
export const hasRemainingTreatmentsToDecide = (medRequest: MedRequest, action: FamilyDecision) => {
  const { isOpenedTarget, rows } = familyDecisionTargetsOf(medRequest, action)
  return isOpenedTreatmentActioned(medRequest) && !isOpenedTarget && rows.length > 0
}

/** Ids of the treatments still awaiting a decision, in card order. */
export const pendingMultiProductOrderTreatmentIds = (medRequest: MedRequest): string[] => [
  ...(isOpenedTreatmentPending(medRequest) ? [medRequest.id] : []),
  ...pendingMultiProductOrderFamilyRows(medRequest).map(row => row.id)
]

/** Submit decision sends the whole order, so every pending treatment needs a decision on its card. */
export const hasDecisionForEveryPendingTreatment = (
  medRequest: MedRequest,
  treatmentDecisions: Record<string, TreatmentDecision>
) => pendingMultiProductOrderTreatmentIds(medRequest).every(id => !!treatmentDecisions[id])

/** Ids of the treatments whose cards can record a decision, in card order. */
export const decidableMultiProductOrderTreatmentIds = (medRequest: MedRequest): string[] => [
  ...(isMedRequestInEditableStage(medRequest.stage ?? '') ? [medRequest.id] : []),
  ...decidableMultiProductOrderFamilyRows(medRequest).map(row => row.id)
]
